import { useMemo } from "react";
import { AnimatePresence } from "motion/react";
import { Loader } from "@shared/ui/Loader";
import { MotionFade } from "@shared/ui/MotionFade";
import { groupTimeslotsByTime } from "@platform/features/timeslots/utils/groupTimeslotsByTime";
import {
  ContiguousTimeslot,
  Timeslot,
} from "@platform/features/timeslots/timeslots.types";
import SchedulingAvailabilityGridHeaderRow from "../SchedulingAvailabilityGridHeaderRow/SchedulingAvailabilityGridHeaderRow";
import SchedulingAvailabilityGridTimeRow from "../SchedulingAvailabilityGridTimeRow/SchedulingAvailabilityGridTimeRow";

export type Props = {
  className?: string;
  isLoading: boolean;
  timeSlots: Timeslot[];
  selectedTimeSlots: Timeslot[];
  contiguousTimeslots: ContiguousTimeslot[];
  handleMouseDown: (e: React.MouseEvent, slot: Timeslot) => void;
  handleMouseOver: (e: React.MouseEvent, slot: Timeslot) => void;
  handleTouchStart: (e: React.TouchEvent, slot: Timeslot) => void;
  handleTouchMove: (e: React.TouchEvent, slot: Timeslot) => void;
  handleTouchEnd: (e: React.TouchEvent, slot: Timeslot) => void;
};

export function SchedulingAvailabilityGrid({
  isLoading,
  timeSlots,
  selectedTimeSlots,
  contiguousTimeslots,
  handleMouseDown,
  handleMouseOver,
  handleTouchStart,
  handleTouchMove,
  handleTouchEnd,
}: Props) {
  // Group time slots by time for rendering grid rows
  const timeSlotsByTime = useMemo(
    () => groupTimeslotsByTime(timeSlots),
    [timeSlots]
  );

  return (
    <div className="relative overflow-x-auto w-full">
      <div className="min-h-[600px] relative">
        <AnimatePresence mode="wait">
          {isLoading ? (
            <MotionFade
              className="flex justify-center items-center h-full w-full col-span-full row-span-full"
              key="loader"
              layout
            >
              <Loader />
            </MotionFade>
          ) : (
            <MotionFade key="grid" layout>
              <div
                className="availability-grid min-w-[600px] grid grid-cols-[48px_repeat(7,minmax(40px,1fr))]"
                role="grid"
                aria-label="Weekly availability selection grid"
                aria-multiselectable="true"
              >
                <SchedulingAvailabilityGridHeaderRow />

                {timeSlotsByTime.map(([time, slots], idx) => {
                  return (
                    <div key={idx} className="contents">
                      <SchedulingAvailabilityGridTimeRow
                        formattedTime={time}
                        timeSlots={slots}
                        selectedTimeSlots={selectedTimeSlots}
                        contiguousTimeslots={contiguousTimeslots}
                        handleMouseDown={handleMouseDown}
                        handleMouseOver={handleMouseOver}
                        handleTouchStart={handleTouchStart}
                        handleTouchMove={handleTouchMove}
                        handleTouchEnd={handleTouchEnd}
                        timeIndex={idx}
                      />
                    </div>
                  );
                })}
              </div>
            </MotionFade>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
}

export default SchedulingAvailabilityGrid;
